/**
 * The week at a glance. Dinner is the slot that matters most, so it's always
 * visible; breakfast and lunch are one tap away for the weeks that need them.
 */
import { useCallback, useEffect, useState } from 'react';
import type { Meal, MealPlanEntry } from '../../shared/types';
import { monthDay, todayIso, weekDays, weekdayName } from './dates';
import MealPicker from './MealPicker';
import { errMsg, mealsApi, type Slot } from './mealsApi';
import {
  EmptyState,
  ErrorNote,
  Loading,
  MealPhoto,
  WeekNav,
  btnGhost,
  btnPrimary,
  btnSecondary,
  cardCls,
} from './ui';

const SLOT_LABEL: Record<Slot, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
};

export default function WeekPlanner({
  week,
  meals,
  onWeek,
  onNewMeal,
  onPlanChanged,
}: {
  week: string;
  meals: Meal[];
  onWeek: (w: string) => void;
  onNewMeal: () => void;
  onPlanChanged: () => void;
}) {
  const [plan, setPlan] = useState<MealPlanEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [allSlots, setAllSlots] = useState(false);
  const [picking, setPicking] = useState<{ date: string; slot: Slot } | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setPlan(await mealsApi.getPlan(week));
    } catch (err) {
      setError(errMsg(err));
    }
  }, [week]);

  useEffect(() => {
    setPlan(null);
    void load();
  }, [load]);

  const slots: Slot[] = allSlots ? ['breakfast', 'lunch', 'dinner'] : ['dinner'];
  const today = todayIso();

  async function pick(meal: Meal) {
    if (!picking) return;
    setBusy(true);
    setError(null);
    try {
      await mealsApi.setPlan({ plan_date: picking.date, slot: picking.slot, meal_id: meal.id });
      setPicking(null);
      await load();
      onPlanChanged();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  async function clear(entry: MealPlanEntry) {
    setBusy(true);
    setError(null);
    try {
      await mealsApi.clearPlan(entry.id);
      await load();
      onPlanChanged();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  const picker = picking ? (
    <MealPicker
      title={`${SLOT_LABEL[picking.slot]} — ${weekdayName(picking.date, true)}`}
      meals={meals}
      onPick={pick}
      onNewMeal={() => {
        setPicking(null);
        onNewMeal();
      }}
      onClose={() => setPicking(null)}
    />
  ) : null;

  return (
    <div className="space-y-4">
      <div className={`${cardCls} p-3`}>
        <WeekNav week={week} onWeek={onWeek} />
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-ink-soft">
          {plan === null
            ? 'Loading the week…'
            : `${plan.length} meal${plan.length === 1 ? '' : 's'} planned`}
        </p>
        <button
          type="button"
          className={btnSecondary}
          aria-pressed={allSlots}
          onClick={() => setAllSlots((v) => !v)}
        >
          {allSlots ? 'Dinners only' : 'All three meals'}
        </button>
      </div>

      {error && <ErrorNote message={error} onRetry={load} />}

      {plan === null ? (
        <Loading label="Loading this week's plan" />
      ) : meals.length === 0 ? (
        <EmptyState
          title="Nothing to plan with yet"
          body="Add a few meals your family actually eats. Once they're in the box, planning a week takes about a minute."
          action={
            <button type="button" className={btnPrimary} onClick={onNewMeal}>
              Add your first meal
            </button>
          }
        />
      ) : (
        // The week reads as a grid of day cards, like the home bento: one column on
        // a kitchen phone, two on a tablet, three on desktop (7 cells fill 3 + 3 + 1),
        // each roomy enough to lead with the meal's photo. Gap lives on the grid;
        // `[&>*]:h-full` stretches each day's card to its row height so cells align
        // flush top and bottom even when "All three meals" makes some taller.
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {weekDays(week).map((date) => {
            const isToday = date === today;
            return (
              <li key={date} className="[&>*]:h-full">
                <article
                  className={`${cardCls} flex flex-col p-3 ${isToday ? 'border-sage bg-sage-soft/40' : ''}`}
                  aria-label={`${weekdayName(date, true)} ${monthDay(date)}`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <h3 className="font-display text-base font-semibold text-ink">
                      {weekdayName(date, true)}{' '}
                      <span className="font-sans text-sm font-normal text-ink-faint">
                        {monthDay(date)}
                      </span>
                    </h3>
                    {isToday && (
                      <span className="rounded-full bg-sage px-2 py-0.5 text-xs font-medium text-white">
                        Today
                      </span>
                    )}
                  </div>

                  <div className="mt-2 space-y-2">
                    {slots.map((slot) => {
                      const entry = plan.find((p) => p.plan_date === date && p.slot === slot);
                      if (!entry) {
                        return (
                          <button
                            key={slot}
                            type="button"
                            disabled={busy}
                            onClick={() => setPicking({ date, slot })}
                            className="flex min-h-[44px] w-full items-center gap-2 rounded-xl border border-dashed border-border px-3 text-left text-sm text-ink-soft transition-colors hover:border-sage hover:text-sage focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage disabled:opacity-50"
                          >
                            <span aria-hidden="true">＋</span>
                            Add {SLOT_LABEL[slot].toLowerCase()}
                          </button>
                        );
                      }
                      return (
                        // The photo leads: a full-width banner across the top of the slot,
                        // meal name and actions stacked beneath. In a 3-up cell the image is
                        // the fastest way to read the week at a glance ("oh, taco night"),
                        // and the name owns its own full-width line, so a one-word dinner
                        // ("Chili") is never squeezed by anything beside it.
                        <div key={slot} className="overflow-hidden rounded-xl bg-canvas">
                          <MealPhoto
                            name={entry.meal?.name ?? 'Meal'}
                            url={entry.meal?.photo_url ?? null}
                            className="aspect-[3/2] w-full"
                          />
                          <div className="p-2.5">
                            <p className="truncate font-medium text-ink">
                              {entry.meal?.name ?? 'Meal'}
                            </p>
                            <p className="text-xs text-ink-faint">{SLOT_LABEL[slot]}</p>
                            <div className="mt-2 flex justify-end gap-1">
                              <button
                                type="button"
                                className={btnGhost}
                                disabled={busy}
                                onClick={() => setPicking({ date, slot })}
                              >
                                Change
                              </button>
                              <button
                                type="button"
                                className={btnGhost}
                                disabled={busy}
                                aria-label={`Remove ${entry.meal?.name ?? 'meal'} from ${SLOT_LABEL[
                                  slot
                                ].toLowerCase()} on ${weekdayName(date, true)}`}
                                onClick={() => clear(entry)}
                              >
                                <span aria-hidden="true">✕</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </article>
              </li>
            );
          })}
        </ul>
      )}

      {picker}
    </div>
  );
}
